/* eslint-disable no-unused-vars */
/*
 * <license header>
 */

import React, { useState, useEffect } from 'react';
import { Provider, defaultTheme } from '@adobe/react-spectrum';
import ErrorBoundary from 'react-error-boundary';
import { HashRouter, Switch, Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import actionWebInvoke from './utils/actionCallUtils';
import actions from './config.json';
import OBJCODE from './constants';

import Header from './components/Layout/Header';
import TaskDetailsPage from './pages/TaskDetails';
import ProjectBrief from './pages/Brief';
import Home from './pages/Home';
import Search from './pages/Search/Search';
import Calendar from './pages/Calendar';
import { NavbarSearchProvider } from './hooks/searchContext';
import MyBoards from './pages/Boards/MyBoards';
import Request from './pages/Request';
import DynamicRequestForm from './pages/DynamicRequestForm';
import Settings from './pages/Settings';
import DynamicProjectForm from './pages/DynamicProjectForm';

const componentLogs = props => {
  const { runtime, ims } = props;

  // console.log('runtime object:', runtime);
  // console.log('ims object:', ims);

  // use exc runtime event handlers
  // respond to configuration change events (e.g. user switches org)
  runtime.on('configuration', ({ imsOrg, imsToken, locale }) => {
    console.log('configuration change', { imsOrg, imsToken, locale });
  });

  // respond to history change events
  runtime.on('history', ({ type, path }) => {
    // console.log('history change', { type, path });
  });
};

function App(props) {
  componentLogs(props);
  const { runtime, ims } = props;

  const [state, setState] = useState({ isLoading: true, elements: [] });
  const [userID, setUserID] = useState('');

  async function isAdmininstrator() {
    const filterRoles = ims.profile.roles.filter(
      item =>
        item.named_role === 'USER_GROUP_ADMIN' &&
        item.target_data.group_name === 'Symphony User Admins',
    );
    const isAdmin = filterRoles.length === 1;
    setState({ ...state, isAdmin });
    // console.log('isAdmin', isAdmin);
  }

  async function getUserID() {
    setState({ ...state, isLoading: true });
    const fields = ['ID', 'name'];
    const queryParams = { emailAddr: ims.profile.email };
    const actionResponse = await actionWebInvoke(
      actions['workfront-action'],
      {},
      { objCode: OBJCODE.USER, queryParams, fields },
    );
    setUserID(actionResponse[0].ID);
  }

  useEffect(() => {
    getUserID();
    isAdmininstrator();
  }, [ims]);

  // error handler on UI rendering failure
  function onError(e, componentStack) {
    console.log('e, componentStack: ', e, componentStack);
  }

  // component to show if UI fails rendering
  function fallbackComponent({ componentStack, error }) {
    return (
      <>
        <h1 style={{ textAlign: 'center', marginTop: '20px' }}>
          Something went wrong :(
        </h1>
        <pre>{`${componentStack}\n${error.message}`}</pre>
      </>
    );
  }
  return (
    <ErrorBoundary onError={onError} FallbackComponent={fallbackComponent}>
      <HashRouter>
        <Provider theme={defaultTheme} colorScheme="light">
          <NavbarSearchProvider ims={ims}>
            <Header isAdmin={state.isAdmin} />
            <Switch>
              <Route exact path="/">
                <Home
                  runtime={runtime}
                  ims={ims}
                  user={userID}
                  userEmail={ims.profile.email}
                />
              </Route>
              <Route exact path="/task/:ID">
                <TaskDetailsPage
                  runtime={runtime}
                  ims={ims}
                  user={userID}
                  userEmail={ims.profile.email}
                />
              </Route>
              <Route path="/search">
                <Search ims={ims} />
              </Route>
              <Route path="/brief">
                <ProjectBrief runtime={runtime} ims={ims} user={userID} />
              </Route>
              <Route path="/request">
                <Request runtime={runtime} ims={ims} user={userID} />
              </Route>
              <Route path="/dynamicrequest">
                <DynamicRequestForm runtime={runtime} ims={ims} user={userID} />
              </Route>
              <Route path="/dynamicbrief">
                <DynamicProjectForm runtime={runtime} ims={ims} user={userID} />
              </Route>
              <Route path="/calendar">
                <Calendar runtime={runtime} ims={ims} user={userID} />
              </Route>
              <Route path="/my-boards">
                <MyBoards runtime={runtime} ims={ims} user={userID} />
              </Route>
              <Route path="/settings">
                <Settings
                  isAdmin={state.isAdmin}
                  runtime={runtime}
                  ims={ims}
                  user={userID}
                  setUserID={setUserID}
                />
              </Route>
            </Switch>
          </NavbarSearchProvider>
        </Provider>
      </HashRouter>
    </ErrorBoundary>
  );
}

App.propTypes = {
  ims: PropTypes.shape({
    profile: PropTypes.shape({
      account_type: PropTypes.string,
      displayName: PropTypes.string,
      session: PropTypes.string,
      email: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    org: PropTypes.string,
    token: PropTypes.string,
  }),
  runtime: PropTypes.shape({}).isRequired,
};

App.defaultProps = {
  ims: {
    profile: {
      account_type: '',
      displayName: '',
      session: '',
      email: '',
      roles: '',
    },
    org: '',
    token: '',
  },
};

export default App;
